import { Box, Chip, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { primary } from '@src/muithemes/b2ChatTheme';
import { fetchContactTags, TagsPaginationPayload } from '@src/reducers/tags';
import { getContactTags, getContactTagsPagination } from '@src/selectors/tags';
import { reachedEndOfList, THRESHOLD_50 } from '@src/utils/scrollUtils';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AnyTag } from '../../Tags/types';
import './TagsSelect.style.css';

const INIT_PAGE = 1;
const MIN_PAGE_SIZE_TO_HAVE_SCROLL = 20;

export type IContactTagsSelect = {
  onChange: (tags: AnyTag[]) => void;
  value: AnyTag[];
  inputLabel: string;
  getTagOption: <T extends AnyTag>(item: T) => TagItem;
};

type TagItem = {
  id: number | string;
  color?: string;
  label?: string;
  emoji?: string;
};

const ContactTagsSelect: React.FC<IContactTagsSelect> = ({
  value = [],
  onChange,
  inputLabel,
  getTagOption,
}) => {
  const dispatch = useDispatch();
  const listRef = useRef<HTMLElement | null>(null);

  const [pagination, setPagination] = useState<TagsPaginationPayload>({
    pageNum: INIT_PAGE,
    pageSize: MIN_PAGE_SIZE_TO_HAVE_SCROLL,
    name: '',
  });

  const [inputValue, setInputValue] = useState('');
  const [hasNextPage, setHasNextPage] = useState(false);
  const [contactTags, setContactTags] = useState<AnyTag[]>([]);

  const retrievedContactTags = useSelector(getContactTags);
  const paginationResponse = useSelector(getContactTagsPagination);

  useEffect(() => {
    const timer = setTimeout(() => {
      setPagination(prev => ({
        ...prev,
        pageNum: INIT_PAGE,
        name: inputValue,
      }));
    }, 400);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputValue]);

  useEffect(() => {
    dispatch(fetchContactTags({ type: 'CONTACT', pagination }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination]);

  useEffect(() => {
    setHasNextPage(paginationResponse.hasNextPage);
  }, [paginationResponse]);

  useEffect(() => {
    if (pagination.pageNum === INIT_PAGE) {
      setContactTags(retrievedContactTags);
    } else {
      setContactTags([...contactTags, ...retrievedContactTags]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retrievedContactTags]);

  return (
    <Autocomplete
      multiple
      value={value}
      inputValue={inputValue}
      onInputChange={(_event, newVal, reason) => {
        if (reason === 'input') {
          setInputValue(newVal);
        }
      }}
      options={contactTags}
      onChange={(_, cTags) => onChange(cTags)}
      disabled={contactTags.length === 0 && !inputValue}
      getOptionLabel={tag => {
        const { emoji, label } = getTagOption(tag);
        return `${emoji} ${label}`;
      }}
      getOptionSelected={selectedItem => {
        const selectedItemId = getTagOption(selectedItem).id;
        return value.some(item => getTagOption(item).id === selectedItemId);
      }}
      renderInput={params => (
        <TextField {...params} variant="outlined" label={inputLabel} />
      )}
      renderTags={(tagsValue, getTagProps) => (
        <>
          {tagsValue.slice(0, 3).map((tag, index) => {
            const { label, color, emoji } = getTagOption(tag);
            return (
              <Chip
                key={label}
                {...getTagProps({ index })}
                label={`${label} ${emoji}`}
                className="tags-chip"
                size="small"
                style={{
                  backgroundColor: color,
                  color: 'rgba(0,0,0,0.87)',
                  marginBottom: -1,
                  marginTop: -1,
                }}
              />
            );
          })}
          {tagsValue.length > 3 && (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              style={{
                height: 18.5,
                maxWidth: 28,
                minWidth: 28,
                marginLeft: 3,
                fontWeight: 600,
                fontSize: '.8rem',
                borderRadius: 100,
                cursor: 'default',
                backgroundColor: primary.main,
                color: '#fff',
              }}
            >
              +{tagsValue.slice(3).length}
            </Box>
          )}
        </>
      )}
      ListboxProps={{
        ref: listRef,
        onScroll: () => {
          if (reachedEndOfList(listRef, THRESHOLD_50) && hasNextPage) {
            setPagination(prev => ({
              ...prev,
              pageNum: prev.pageNum + 1,
            }));
            setHasNextPage(false);
          }
        },
      }}
    />
  );
};

export default ContactTagsSelect;
